import { useState, useRef } from 'react';
import ScrollReveal from '../ScrollReveal';
import Lightbox from './Lightbox';
import GalleryCarousel from './GalleryCarousel';
import galleryItems from '@/data/gallery';
import { t } from '@/data/translations';
import type { Lang } from '@/pages/Index';

const Gallery = ({ lang }: { lang: Lang }) => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const tr = t(lang).gallery;

  // Map gallery data indices to caption keys
  const captionKeys = ['video', 'illu01', 'illu04', 'illu02', 'foulards'];

  const openLightbox = (index: number) => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setLightboxIndex(index);
  };


  return (
    <section id="gallery" className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div className="max-w-4xl mx-auto">
        <ScrollReveal>
          <div className="section-divider mb-8" />
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <h2 className="text-sm tracking-widest uppercase text-muted-foreground mb-12">{tr.sectionTitle}</h2>
        </ScrollReveal>

        {/* Masonry grid – items rendered in exact array order */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 lg:gap-6 [column-fill:_balance]">
          {galleryItems.map((item, index) => {
            const captionKey = captionKeys[index];

            if (item.type === 'video') {
              return (
                <ScrollReveal key={index} delay={200}>
                  <div className="break-inside-avoid mb-4 lg:mb-6">
                    <div
                      className="relative w-full rounded-sm bg-muted border border-border overflow-hidden cursor-pointer aspect-[9/16] transition-all duration-300 hover:brightness-75"
                      onClick={() => openLightbox(index)}
                    >
                      {!videoLoaded && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <div className="w-6 h-6 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                        </div>
                      )}
                      <video
                        ref={videoRef}
                        src={item.src}
                        className="w-full h-full object-cover"
                        loop
                        playsInline
                        preload="metadata"
                        onLoadedData={() => setVideoLoaded(true)}
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                      />
                      {videoLoaded && !isPlaying && (
                        <div className="absolute inset-0 flex items-center justify-center bg-background/20 transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center">
                            <span className="text-foreground text-lg ml-0.5">▶</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {captionKey && tr.captions[captionKey] && (
                      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                        {tr.captions[captionKey]}
                      </p>
                    )}
                  </div>
                </ScrollReveal>
              );
            }

            if (item.type === 'carousel') {
              return (
                <ScrollReveal key={index} delay={200 + index * 100}>
                  <div className="break-inside-avoid mb-4 lg:mb-6">
                    <GalleryCarousel
                      slides={item.slides!}
                      alt={item.alt}
                      onClickSlide={() => openLightbox(index)}
                    />
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                      {captionKey && tr.captions[captionKey] ? tr.captions[captionKey] : ''}
                      {item.captionLink && (
                        <>
                          {' '}{tr.moreInfo}{' '}
                          <a
                            href={item.captionLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-foreground transition-colors duration-300"
                          >
                            {item.captionLink.label}
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                </ScrollReveal>
              );
            }

            // image
            return (
              <ScrollReveal key={index} delay={200 + index * 100}>
                <div className="break-inside-avoid mb-4 lg:mb-6">
                  <div
                    className="rounded-sm border border-border overflow-hidden transition-all duration-300 hover:brightness-75 cursor-pointer"
                    onClick={() => openLightbox(index)}
                  >
                    <img src={item.src} alt={item.alt || ''} className="w-full h-auto" loading="lazy" />
                  </div>
                  {captionKey && tr.captions[captionKey] && (
                    <p className="mt-3 text-xs text-muted-foreground leading-relaxed">
                      {tr.captions[captionKey]}
                    </p>
                  )}
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>

      <Lightbox
        items={galleryItems}
        currentIndex={lightboxIndex}
        isOpen={lightboxIndex >= 0}
        onClose={() => setLightboxIndex(-1)}
        onNavigate={setLightboxIndex}
        lang={lang}
        captionKeys={captionKeys}
      />
    </section>
  );
};

export default Gallery;
